import {
  Alert,
  Box,
  Button,
  FormControl,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
} from "@mui/material";
import React from "react";
import classNames from "classnames";
import localforage from "localforage";
import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { HIGHLIGHT_COLORS } from "../Layouts/LayoutPrice";
import { useMerchantFollow } from "../../contexts/MerchantFollowContext";
import FilterBar, { FilterConfig } from "./FilterBar";
import { useExchangeAdapter } from "../../contexts/ExchangeContext";
import type { P2PAd, P2PAdDetail } from "../../exchanges/domain";

const VIP_LEVEL_CHENH = 45;

interface TextPriceProps {
  data: P2PAd;
  showNickname: boolean;
  transAmount: string;
  isStale?: boolean;
}

const TextPrice = React.memo((props: TextPriceProps) => {
  const adapter = useExchangeAdapter();
  let accessLevel = window.location.href.includes("premium") ? 1 : 0;
  accessLevel = window.location.href.includes("admin") ? 2 : accessLevel;

  const { data, showNickname, isStale } = props;

  const fake = adapter.isFakeAd(data);

  const formatedText = formatMoney(data.price);

  const userType = data.advertiser.userType === "user" ? "user" : "merchant";

  const hasBankPaymentMethod = React.useMemo(
    () =>
      data.tradeMethods.some((item) =>
        item.payType?.toLowerCase().includes("bank"),
      ),
    [data.tradeMethods],
  );

  const isKycRequired = data.takerAdditionalKycRequired === 1;

  const vipClass = adapter.getVipStyle(data, accessLevel);

  const signals = React.useMemo(
    () =>
      adapter.getPriceSignals(data, accessLevel, {
        isKycRequired,
        hasBankPaymentMethod,
        transAmount: Number(props.transAmount) || 0,
      }),
    [
      adapter,
      data,
      accessLevel,
      isKycRequired,
      hasBankPaymentMethod,
      props.transAmount,
    ],
  );

  const TitleTooltip = () => {
    const min = translateNumberToAcronym(data.minSingleTransAmount);
    const max = translateNumberToAcronym(data.dynamicMaxSingleTransAmount);
    const logo = adapter.getLogo(data);
    function shortenString(str: string) {
      if (str.length > 30) {
        return str.substring(0, 30) + "...";
      } else {
        return str;
      }
    }

    const chenhHoaVon = Math.round(
      data.commissionRate * +data.price * 2 +
        (data.tradeType === "SELL" ? 0.001 * +data.price : 0),
    );
    const chenhBreakEvenPrice =
      data.tradeType === "SELL"
        ? +data.price - chenhHoaVon
        : chenhHoaVon + +data.price;

    const isLowRate = data.advertiser.positiveRate < 0.97;
    const isVeryLowRate = data.advertiser.positiveRate < 0.95;
    const isBadFinish = data.advertiser.monthFinishRate < 0.99;

    const positiveRateColor = classNames({
      "text-emerald-300": !isLowRate,
      "text-yellow-400": isLowRate && !isVeryLowRate,
      "text-pink-400 font-bold": isVeryLowRate,
    });

    const chenhColor = classNames({
      "text-cyan-200": chenhHoaVon < VIP_LEVEL_CHENH,
      "text-yellow-400": chenhHoaVon < VIP_LEVEL_CHENH,
      "text-pink-400 font-bold": chenhHoaVon > VIP_LEVEL_CHENH,
    });

    return (
      <div className="min-w-[220px] max-w-[320px] space-y-1.5">
        {/* ── Header: Merchant ── */}
        <div className="flex items-center gap-1 flex-wrap">
          {(() => {
            const link = adapter.getAdvertiserLink?.(data.advertiser.userNo);
            if (link) {
              return (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-blue-300 font-semibold text-white text-[12px]"
                >
                  {logo} {shortenString(data.advertiser.nickName)}
                </a>
              );
            }
            return (
              <span className="hover:text-blue-300 font-semibold text-white text-[12px]">
                {logo} {shortenString(data.advertiser.nickName)}
              </span>
            );
          })()}
          <span
            className="cursor-pointer text-[10px] hover:text-blue-300"
            onClick={handleCopyNickname}
            title="Sao chép nickname"
          >
            📋
          </span>
        </div>

        {/* ── Quick stats row ── */}
        <div className="flex flex-wrap items-center gap-1">
          {accessLevel >= 1 && (
            <span className="inline-flex items-center hover:text-blue-300">
              <span
                className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  data.advertiser.activeTimeInSecond > 60
                    ? "bg-zinc-700/80 text-zinc-400"
                    : "bg-emerald-900/60 text-emerald-300"
                }`}
              >
                {data.advertiser.activeTimeInSecond > 60 ? "🔘" : "🟢"}{" "}
                {formatTimeAgo(data.advertiser.activeTimeInSecond)}
              </span>
            </span>
          )}
          <span
            className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium ${
              isBadFinish
                ? "bg-red-900/50 text-red-300"
                : "bg-emerald-900/50 text-emerald-300"
            }`}
          >
            {isBadFinish && " 👎🛑 "}
            {Math.floor(data.advertiser.monthFinishRate * 1000) / 10}%
          </span>
          <span className="inline-flex items-center bg-zinc-700/60 text-zinc-300 text-[10px] px-1.5 py-0.5 rounded">
            {formatMoney(data.advertiser.monthOrderCount)} đơn
          </span>
          <span
            className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded ${positiveRateColor}`}
          >
            {isLowRate && " 👎"}
            {isVeryLowRate && " 🛑 "}
            Tích cực {Math.floor(data.advertiser.positiveRate * 1000) / 10}%
          </span>
        </div>

        {/* ── Key metrics ── */}
        <div className="flex items-center justify-between bg-zinc-800/40 rounded px-2 py-0.5">
          <span className="text-[10px] text-zinc-400">Khả dụng</span>
          <span className="text-[11px] text-white font-semibold">
            ${formatMoney(data.tradableQuantity)}
          </span>
        </div>
        <div className="flex items-center justify-between bg-zinc-800/40 rounded px-2 py-0.5">
          <span className="text-[10px] text-zinc-400">Giới hạn</span>
          <span className="text-[11px] text-white font-semibold">
            {min} — {max}
          </span>
        </div>

        {/* ── Premium section (accessLevel >= 1) ── */}
        {accessLevel >= 1 && (
          <>
            <div
              className={`flex items-center justify-between bg-zinc-800/40 rounded px-2 py-0.5 ${chenhColor}`}
            >
              <span className="text-[10px]">Chênh hòa vốn</span>
              <span className="text-[11px] font-bold">
                {Math.round(data.commissionRate * +data.price * 2 * 10) / 10}{" "}
                (⚡{chenhBreakEvenPrice})
              </span>
            </div>

            {data.minSingleTransAmount >= 700000000 && (
              <div className="flex items-center justify-between bg-zinc-800/40 rounded px-2 py-0.5">
                <span className="text-[10px] text-zinc-400">Bán lẻ (5+20)</span>
                <span className="text-[11px] text-white font-semibold">
                  ✨
                  {data.tradeType === "SELL"
                    ? +data.price -
                      Math.round(data.commissionRate * +data.price + 40 / 2)
                    : Math.round(data.commissionRate * +data.price + 40 / 2) +
                      +data.price}
                </span>
              </div>
            )}

            {data.minSingleTransAmount >= 700000000 && (
              <div className="flex items-center justify-between bg-zinc-800/40 rounded px-2 py-0.5">
                <span className="text-[10px] text-zinc-400">Mua lẻ (-15)</span>
                <span className="text-[11px] text-white font-semibold">
                  ✨
                  {data.tradeType === "SELL"
                    ? +data.price + 15
                    : +data.price - 15}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between bg-zinc-800/40 rounded px-2 py-0.5">
              <span className="text-[10px] text-zinc-400">Thời hạn</span>
              <span className="text-[11px] text-blue-300 hover:text-blue-200 font-semibold">
                {data.payTimeLimit} phút
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[10px] text-zinc-400">💳</span>
              {data.tradeMethods.map((item) => (
                <span
                  key={item.payType}
                  className="bg-zinc-700/70 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded"
                >
                  {item.payType}
                </span>
              ))}
            </div>
          </>
        )}

        {/* ── Admin section (accessLevel >= 2) ── */}
        {accessLevel >= 2 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="bg-zinc-700/70 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded">
              KYC: {data.takerAdditionalKycRequired}
            </span>
            <span className="bg-zinc-700/70 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded">
              VIP: {data.advertiser.vipLevel}
            </span>
            <span className="bg-zinc-700/70 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
              ID:{" "}
              {(() => {
                const link = adapter.getLink?.(data.advNo);
                if (link) {
                  return (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-blue-300"
                    >
                      {data.advNo}
                    </a>
                  );
                }
                return (
                  <span className="hover:text-blue-300">{data.advNo}</span>
                );
              })()}
              <span
                className="cursor-pointer"
                onClick={() => {
                  navigator.clipboard.writeText(data.advNo);
                }}
              >
                📋
              </span>
            </span>
          </div>
        )}

        {/* ── Phân cách: Chi tiết nâng cao (accessLevel >= 1) ── */}
        {accessLevel >= 1 && (
          <>
            <hr className="border-zinc-600 my-1.5" />
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-[10px] uppercase tracking-wide font-semibold">
                Chi tiết nâng cao
              </span>
              <button
                type="button"
                className={advancedStatusButtonClassName}
                onClick={handleFetchAdvancedClick}
                disabled={isLoadingAdvDetail}
              >
                {advancedStatusButtonText}
              </button>
            </div>
            {advancedAdvInfo && (
              <div className="flex flex-wrap items-center gap-1 mt-0.5">
                <span className="bg-blue-900/50 text-blue-300 text-[10px] px-1.5 py-0.5 rounded font-semibold">
                  Khớp {formatAmount(advancedAdvInfo.tradedAmount)}
                </span>
                {advancedAdvInfo.allowTradeMerchant === 1 && (
                  <span className="bg-emerald-900/50 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded">
                    ✓ Merchant
                  </span>
                )}
                {(() => {
                  const days = advancedAdvInfo.buyerRegDaysLimit;
                  if (days != null && Number(days) >= 0) {
                    return (
                      <span className="bg-amber-900/50 text-amber-300 text-[10px] px-1.5 py-0.5 rounded">
                        {Number(days)}d DK
                      </span>
                    );
                  }
                  return null;
                })()}
                {(() => {
                  const btc = advancedAdvInfo.buyerBtcPositionLimit;
                  if (btc != null && Number(btc) >= 0) {
                    return (
                      <span className="bg-amber-900/50 text-amber-300 text-[10px] px-1.5 py-0.5 rounded">
                        ≤{Number(btc).toFixed(8)} BTC
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            {advDetailSection && <div className="mt-1">{advDetailSection}</div>}
          </>
        )}
      </div>
    );
  };

  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const followingMerchants = useMerchantFollow();
  const [isLoadingAdvDetail, setIsLoadingAdvDetail] = React.useState(false);
  const [advDetailData, setAdvDetailData] = React.useState<P2PAdDetail | null>(
    null,
  );
  const [advDetailError, setAdvDetailError] = React.useState<string | null>(
    null,
  );
  const [isRemarksExpanded, setIsRemarksExpanded] = React.useState(false);

  const formatUtc7 = React.useCallback((timestamp: number) => {
    if (!timestamp) {
      return "-";
    }
    return new Date(timestamp).toLocaleString("vi-VN", {
      timeZone: "Asia/Ho_Chi_Minh",
      hour12: false,
    });
  }, []);

  const getAdvancedAdvInfo = React.useCallback(() => {
    if (!advDetailData?.adv) {
      return null;
    }

    const adv = advDetailData.adv;
    const initAmount = Number(adv.initAmount || 0);
    const surplusAmount = Number(adv.surplusAmount || 0);
    const tradedAmount = initAmount - surplusAmount;
    const abnormalStatusList = Array.isArray(adv.abnormalStatusList)
      ? adv.abnormalStatusList
      : [];

    const remarks = (adv.remarks || "").trim();

    return {
      initAmount,
      surplusAmount,
      tradedAmount,
      createTimeText: formatUtc7(adv.createTime),
      advUpdateTimeText: formatUtc7(adv.advUpdateTime),
      createTimeRaw: adv.createTime,
      advUpdateTimeRaw: adv.advUpdateTime,
      abnormalStatusList,
      hasAbnormalStatus: abnormalStatusList.length > 0,
      buyerBtcPositionLimit: adv.buyerBtcPositionLimit,
      takerAdditionalKycRequired: adv.takerAdditionalKycRequired,
      allowTradeMerchant: adv.allowTradeMerchant,
      buyerRegDaysLimit: adv.buyerRegDaysLimit,
      remarks,
      hasLongRemarks: remarks.length > 160,
    };
  }, [advDetailData, formatUtc7]);

  const advancedAdvInfo = getAdvancedAdvInfo();

  const handleFetchAdvDetail = React.useCallback(async () => {
    if (isLoadingAdvDetail) {
      return;
    }

    setIsLoadingAdvDetail(true);
    setAdvDetailError(null);

    const result = await adapter.getAdDetail(data.advNo);

    if (!result) {
      setAdvDetailData(null);
      setAdvDetailError("Không tải được dữ liệu nâng cao");
      setIsLoadingAdvDetail(false);
      return;
    }

    setAdvDetailData(result);
    setIsRemarksExpanded(false);
    setIsLoadingAdvDetail(false);
  }, [data.advNo, isLoadingAdvDetail, adapter]);

  React.useEffect(() => {
    setAdvDetailData(null);
    setAdvDetailError(null);
    setIsLoadingAdvDetail(false);
    setIsRemarksExpanded(false);
  }, [data.advNo]);

  const hasLoadedAdvancedInfo = !!advancedAdvInfo;

  const [isAdvDetailJustLoaded, setIsAdvDetailJustLoaded] =
    React.useState(false);

  React.useEffect(() => {
    if (hasLoadedAdvancedInfo) {
      setIsAdvDetailJustLoaded(true);
      const timer = setTimeout(() => setIsAdvDetailJustLoaded(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [hasLoadedAdvancedInfo, advDetailData]);

  const handleCopyNickname = React.useCallback(() => {
    navigator.clipboard.writeText(data.advertiser.nickName);
  }, [data.advertiser.nickName]);

  const handleCopyPrice = React.useCallback(() => {
    navigator.clipboard.writeText(data.price);
  }, [data.price]);

  const handleClickCallback = React.useCallback(() => {
    handleCopyPrice();
    handleClick();
  }, [handleCopyPrice]);

  const handleFetchAdvancedClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      void handleFetchAdvDetail();
    },
    [handleFetchAdvDetail],
  );

  const formatAmount = React.useCallback((value: number) => {
    return formatMoney(Math.max(0, Math.round(value)));
  }, []);

  const formatRemarks = React.useCallback(
    (remarks: string, expanded: boolean) => {
      if (!remarks) {
        return "Không có ghi chú";
      }

      if (expanded || remarks.length <= 160) {
        return remarks;
      }

      return `${remarks.slice(0, 160)}...`;
    },
    [],
  );

  const advancedStatusButtonText = isLoadingAdvDetail
    ? "⏳ Đang tải..."
    : hasLoadedAdvancedInfo
      ? "🔄 Tải lại"
      : "🔍 Xem thêm";

  const advancedStatusButtonClassName = classNames(
    "cursor-pointer px-2 py-0.5 rounded text-[11px] font-semibold transition-colors duration-200",
    {
      "text-zinc-400 bg-zinc-700/60": isLoadingAdvDetail,
      "text-blue-300 bg-blue-900/40 hover:bg-blue-800/60 hover:text-blue-200":
        !isLoadingAdvDetail,
    },
  );

  const getAdvancedDetailSection = () => {
    if (advDetailError) {
      return <div className="text-red-300">{advDetailError}</div>;
    }

    if (!advancedAdvInfo) {
      return null;
    }

    const tradedPct =
      advancedAdvInfo.initAmount > 0
        ? Math.min(
            100,
            (advancedAdvInfo.tradedAmount / advancedAdvInfo.initAmount) * 100,
          )
        : 0;

    const highlightClass = isAdvDetailJustLoaded
      ? "ring-2 ring-blue-400/70 ring-offset-1 ring-offset-zinc-900 transition-all duration-1000"
      : "";

    const tradedBarColor =
      tradedPct > 80
        ? "bg-red-500"
        : tradedPct > 50
          ? "bg-yellow-500"
          : "bg-emerald-500";

    const getBooleanBadge = (
      val: unknown,
      trueLabel = "Có",
      falseLabel = "Không",
    ) => {
      const isTrue = val === 1 || val === "1";
      return isTrue ? (
        <span className="inline-flex items-center gap-0.5 bg-emerald-900/60 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded font-semibold">
          ✓ {trueLabel}
        </span>
      ) : (
        <span className="inline-flex items-center gap-0.5 bg-zinc-700/80 text-zinc-400 text-[10px] px-1.5 py-0.5 rounded">
          ✗ {falseLabel}
        </span>
      );
    };

    const formatBtcPosition = () => {
      const val = advancedAdvInfo?.buyerBtcPositionLimit;
      if (val === null || val === undefined) {
        return (
          <span className="bg-zinc-700/80 text-zinc-400 text-[10px] px-1.5 py-0.5 rounded">
            Không rõ
          </span>
        );
      }
      const num = Number(val);
      if (num < 0) {
        return (
          <span className="inline-flex items-center bg-emerald-900/60 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded font-semibold">
            Không giới hạn
          </span>
        );
      }
      return (
        <span className="inline-flex items-center bg-amber-900/60 text-amber-300 text-[10px] px-1.5 py-0.5 rounded font-semibold">
          {num.toFixed(2)} BTC
        </span>
      );
    };

    const formatRegDaysBadge = () => {
      const val = advancedAdvInfo?.buyerRegDaysLimit;
      if (val === null || val === undefined) {
        return (
          <span className="bg-zinc-700/80 text-zinc-400 text-[10px] px-1.5 py-0.5 rounded">
            Không rõ
          </span>
        );
      }
      const num = Number(val);
      if (Number.isNaN(num)) {
        return (
          <span className="bg-zinc-700/80 text-zinc-400 text-[10px] px-1.5 py-0.5 rounded">
            Không rõ
          </span>
        );
      }
      if (num < 0) {
        return (
          <span className="inline-flex items-center bg-emerald-900/60 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded font-semibold">
            Không giới hạn
          </span>
        );
      }
      const isHigh = num >= 30;
      return (
        <span
          className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-semibold ${
            isHigh
              ? "bg-amber-900/60 text-amber-300"
              : "bg-emerald-900/60 text-emerald-300"
          }`}
        >
          {num} ngày
        </span>
      );
    };

    const formatRelativeTime = (timestamp: number) => {
      if (!timestamp) return null;
      const now = Date.now();
      const diffMs = now - timestamp;
      const diffSec = Math.floor(diffMs / 1000);
      if (diffSec < 0) return "vừa xong";
      if (diffSec < 60) return `${diffSec}s trước`;
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin} phút trước`;
      const diffHour = Math.floor(diffMin / 60);
      const remainMin = diffMin % 60;
      if (diffHour < 24) {
        return remainMin > 0
          ? `${diffHour} giờ ${remainMin} phút trước`
          : `${diffHour} giờ trước`;
      }
      const diffDay = Math.floor(diffHour / 24);
      const remainHour = diffHour % 24;
      if (diffDay < 30) {
        return remainHour > 0
          ? `${diffDay} ngày ${remainHour}h trước`
          : `${diffDay} ngày trước`;
      }
      const diffMonth = Math.floor(diffDay / 30);
      const remainDay = diffDay % 30;
      if (diffMonth < 12) {
        return remainDay > 0
          ? `${diffMonth} tháng ${remainDay} ngày trước`
          : `${diffMonth} tháng trước`;
      }
      const diffYear = Math.floor(diffMonth / 12);
      const remainMonth = diffMonth % 12;
      return remainMonth > 0
        ? `${diffYear} năm ${remainMonth} tháng trước`
        : `${diffYear} năm trước`;
    };

    const isSameTimestamp =
      advancedAdvInfo.createTimeRaw &&
      advancedAdvInfo.advUpdateTimeRaw &&
      Math.abs(
        advancedAdvInfo.createTimeRaw - advancedAdvInfo.advUpdateTimeRaw,
      ) < 60000;

    const timeRow = isSameTimestamp ? (
      <div className="bg-amber-900/30 border border-amber-700/40 rounded px-2 py-1">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-amber-400 font-semibold">
            ⚠ Tạo = Cập nhật (chưa sửa)
          </span>
          <span className="text-[11px] text-amber-200 font-bold">
            {advancedAdvInfo.advUpdateTimeText}
          </span>
        </div>
        <div className="text-right">
          <span className="text-[10px] text-amber-400 font-medium">
            {formatRelativeTime(advancedAdvInfo.advUpdateTimeRaw)}
          </span>
        </div>
      </div>
    ) : (
      <div className="bg-zinc-800/40 rounded px-2 py-1 space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-500">Tạo</span>
          <span className="text-[11px] text-zinc-300">
            {advancedAdvInfo.createTimeText}
          </span>
        </div>
        <div className="text-right">
          <span className="text-[10px] text-zinc-500">
            {formatRelativeTime(advancedAdvInfo.createTimeRaw)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-500">Cập nhật</span>
          <span className="text-[11px] text-zinc-300">
            {advancedAdvInfo.advUpdateTimeText}
          </span>
        </div>
        <div className="text-right">
          <span className="text-[10px] text-blue-400">
            {formatRelativeTime(advancedAdvInfo.advUpdateTimeRaw)}
          </span>
        </div>
      </div>
    );

    return (
      <div className={`space-y-1.5 ${highlightClass}`}>
        {/* ── Đã khớp (highlighted) ── */}
        <div className="bg-blue-900/40 border border-blue-700/50 rounded px-2 py-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-blue-300 font-semibold tracking-wide uppercase">
              Đã khớp
            </span>
            <span className="text-sm font-bold text-white">
              {formatAmount(advancedAdvInfo.tradedAmount)} /{" "}
              {formatAmount(advancedAdvInfo.initAmount)}
            </span>
          </div>
          <div className="w-full h-1.5 bg-zinc-700 rounded-full mt-1 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${tradedBarColor}`}
              style={{ width: `${tradedPct}%` }}
            />
          </div>
          <div className="flex justify-between mt-0.5">
            <span className="text-[9px] text-zinc-500">
              Còn: {formatAmount(advancedAdvInfo.surplusAmount)}
            </span>
            <span className="text-[9px] text-zinc-400 font-medium">
              {tradedPct.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* ── Key trading flags (highlighted row) ── */}
        <div className="bg-zinc-800/60 border border-zinc-600/50 rounded px-2 py-1.5 space-y-1">
          <div className="text-[10px] text-zinc-400 font-semibold uppercase tracking-wide mb-1">
            Điều kiện giao dịch
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[10px] text-zinc-400">Merchant:</span>
            {getBooleanBadge(advancedAdvInfo.allowTradeMerchant)}
            <span className="text-[10px] text-zinc-400">Ngày DK:</span>
            {formatRegDaysBadge()}
            <span className="text-[10px] text-zinc-400">Vị thế BTC:</span>
            {formatBtcPosition()}
            <span className="text-[10px] text-zinc-400">KYC+:</span>
            {getBooleanBadge(advancedAdvInfo.takerAdditionalKycRequired)}
          </div>
        </div>

        {/* ── Update time (highlighted) ── */}
        {timeRow}

        {/* ── Risk flags (only if present) ── */}
        {advancedAdvInfo.hasAbnormalStatus && (
          <div className="flex flex-wrap items-center gap-1">
            {advancedAdvInfo.abnormalStatusList.map(
              (status: string, idx: number) => (
                <span
                  key={idx}
                  className="inline-flex items-center bg-red-900/60 border border-red-700/50 text-red-300 text-[10px] px-1.5 py-0.5 rounded font-semibold"
                >
                  ⚠ {status}
                </span>
              ),
            )}
          </div>
        )}

        {/* ── Remarks ── */}
        {advancedAdvInfo.remarks && (
          <div
            className="bg-zinc-800/40 rounded px-2 py-1"
            style={{ maxWidth: 320 }}
          >
            <div
              style={{
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
              className="text-zinc-300 text-[11px]"
            >
              {formatRemarks(advancedAdvInfo.remarks, isRemarksExpanded)}
            </div>
            {advancedAdvInfo.hasLongRemarks && (
              <button
                type="button"
                className="underline text-blue-300 hover:text-blue-100 text-[10px] mt-0.5 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsRemarksExpanded((prev) => !prev);
                }}
              >
                {isRemarksExpanded ? "Thu gọn" : "Mở rộng"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const advDetailSection = getAdvancedDetailSection();

  // Find which merchant matches current advertiser
  const matchedMerchant = React.useMemo(() => {
    return followingMerchants.find((merchant) =>
      data.advertiser.nickName
        .toLowerCase()
        .includes(merchant.nickname.toLowerCase()),
    );
  }, [data.advertiser.nickName, followingMerchants]);

  const priceClassNames = React.useMemo(() => {
    const payTimeOverride = adapter.getPayTimeOverride?.(data, accessLevel);
    const merchantColor = adapter.getMerchantColorRules(data, accessLevel);

    return classNames({
      "cursor-pointer": true,
      "hover:bg-slate-800 rounded-md px-1": true,
      ...(merchantColor ? { [merchantColor]: !fake } : {}),
      "text-yellow-300":
        accessLevel >= 1 &&
        userType === "merchant" &&
        !fake &&
        data.advertiser.vipLevel < 3,
      "!text-yellow-300 underline underline-offset-[3px] opacity-25": fake,

      "text-zinc-400 italic": data.advertiser.activeTimeInSecond > 60 * 5, // offline

      // Dynamic merchant highlight colors
      ...(matchedMerchant
        ? {
            [HIGHLIGHT_COLORS[matchedMerchant.colorIndex].bg]: true,
            [HIGHLIGHT_COLORS[matchedMerchant.colorIndex].text]: true,
          }
        : {}),
      ...(vipClass ? { [vipClass]: true } : {}),
      ...(payTimeOverride ? { [payTimeOverride]: true } : {}),
    });
  }, [data, fake, userType, accessLevel, matchedMerchant, vipClass, adapter]);

  return (
    <div>
      <Tooltip title={<TitleTooltip />} placement="right" arrow>
        <div className={priceClassNames} onClick={handleClickCallback}>
          {signals.map((s, i) => (
            <span key={i} className={s.priceClass || undefined}>
              {s.emoji}
            </span>
          ))}
          <span className="inline-flex items-center gap-1">
            <span
              className={
                isStale
                  ? "animate-[pulse_0.3s_ease-in-out_infinite]"
                  : undefined
              }
            >
              {formatedText}
            </span>
            {showNickname && (
              <span className="bg-gray-600/50 text-[9px] text-gray-300 px-1 py-0.5 rounded whitespace-nowrap">
                {data.advertiser.nickName.slice(0, 4)}
              </span>
            )}
          </span>
        </div>
      </Tooltip>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{ backgroundColor: "success.darker" }}
        >
          Đã sao chép: "{data.price}"
        </Alert>
      </Snackbar>
    </div>
  );
});

const PriceTable = React.memo((props: { asset: string; index: number }) => {
  let accessLevel = window.location.href.includes("premium") ? 1 : 0;
  accessLevel = window.location.href.includes("admin") ? 2 : accessLevel;
  const { asset, index: tableIndex } = props;

  const adapter = useExchangeAdapter();
  const isMerchantPerspective = adapter.isMerchantPerspective();

  const canShowPrice = accessLevel >= 1 || asset === "USDT";
  const rowsLimit = accessLevel >= 1 ? 20 : 10;

  const tableId = `${asset}_${tableIndex}`;

  const [tableData, setTableData] = React.useState<{
    weBuy: P2PAd[];
    weSell: P2PAd[];
    different: number | null;
  }>({
    weBuy: [],
    weSell: [],
    different: null,
  });
  const [progress, setProgress] = React.useState(0);
  const [isStale, setIsStale] = React.useState(false);
  const [isLocalStorageReady, setIsLocalStorageReady] = React.useState(false);
  const [filterConfig, setFilterConfig] = React.useState<FilterConfig>({
    displayMode: "all",
    showNickname: false,
    showExtendedRows: false,
  });

  const usdtDefaults = ["", "10000000", "200000000", "500000000", "800000000"];
  const otherDefaults = ["", "10000000", "200000000", "500000000"];
  const defaults = asset === "USDT" ? usdtDefaults : otherDefaults;
  const defaultAmount = defaults[tableIndex] || "";

  const [currentAmount, setCurrentAmount] = React.useState(defaultAmount);

  // Use shared refresh interval from context
  const { refreshInterval } = useRefreshInterval();

  React.useEffect(() => {
    // Only fetch when localStorage is ready to avoid fetching with wrong transAmount
    if (!isLocalStorageReady) {
      return;
    }

    const fetch = async () => {
      setProgress(1);

      const searchParams = {
        tradeType: 1, // BUY
        transAmount: currentAmount,
        asset,
        isMerchantOnly: filterConfig.displayMode === "onlyMerchant",
        page: 1,
        rows: rowsLimit,
      };

      let finalBuyData: P2PAd[] = [];
      let finalSellData: P2PAd[] = [];

      try {
        const [resBuy, resSell] = await Promise.all([
          adapter.searchAds(searchParams),
          adapter.searchAds({ ...searchParams, tradeType: 0 }), // SELL
        ]);

        finalBuyData = resBuy.data;
        finalSellData = resSell.data;

        // If showExtendedRows is true and accessLevel >= 1, fetch page 2 and concat
        if (filterConfig.showExtendedRows && accessLevel >= 1) {
          const [resBuyPage2, resSellPage2] = await Promise.all([
            adapter.searchAds({ ...searchParams, page: 2 }),
            adapter.searchAds({ ...searchParams, page: 2, tradeType: 0 }),
          ]);

          finalBuyData = [...finalBuyData, ...resBuyPage2.data];
          finalSellData = [...finalSellData, ...resSellPage2.data];
        }
      } catch (err) {
        // Fetch failed — keep old data, mark as stale
        console.error(`[PriceTable ${asset}] fetch failed:`, err);
        setIsStale(true);
        setProgress(0);
        return;
      }

      const newData = {
        weBuy: finalBuyData,
        weSell: finalSellData,
        different:
          finalBuyData[1]?.price && finalSellData[1]?.price
            ? Math.floor(+finalBuyData[1].price - +finalSellData[1].price)
            : null,
      };

      setTableData(newData);
      setProgress(0);
      setIsStale(false);
    };
    fetch();

    const clock = setInterval(fetch, 1000 * refreshInterval);

    return () => {
      clearInterval(clock);
    };
  }, [
    asset,
    currentAmount,
    filterConfig,
    refreshInterval,
    isLocalStorageReady,
    adapter,
    accessLevel,
    rowsLimit,
  ]);

  // Cache for localforage values
  const localStorageCache = React.useRef<Record<string, any>>({});

  React.useEffect(() => {
    // Reset ready state when tableId changes to wait for new localStorage data
    setIsLocalStorageReady(false);

    const getLocalData = async () => {
      // Check cache first
      if (localStorageCache.current[tableId] !== undefined) {
        setCurrentAmount(localStorageCache.current[tableId]);
        setIsLocalStorageReady(true);
        return;
      }

      const res = await localforage.getItem(tableId);
      if (res) {
        localStorageCache.current[tableId] = res;
        setCurrentAmount(String(res));
      }
      // Always mark as ready after checking localStorage (whether found or not)
      setIsLocalStorageReady(true);
    };
    getLocalData();
  }, [tableId]);

  // Persist filterConfig to localforage
  const handleFilterPersist = React.useCallback(
    (newConfig: Partial<FilterConfig>) => {
      const filterKey = `${tableId}_filter`;
      setFilterConfig((prev) => {
        const updated = { ...prev, ...newConfig };
        // Persist to localforage
        localforage.setItem(filterKey, updated).catch(() => {});
        return updated;
      });
    },
    [tableId],
  );

  // Load saved filterConfig on mount
  React.useEffect(() => {
    const loadFilter = async () => {
      const filterKey = `${tableId}_filter`;
      const saved = await localforage.getItem(filterKey);
      if (saved && typeof saved === "object") {
        const typed = saved as FilterConfig;
        setFilterConfig({
          displayMode: typed.displayMode || "all",
          showNickname: typed.showNickname ?? false,
          showExtendedRows: typed.showExtendedRows ?? false,
        });
      }
    };
    loadFilter();
  }, [tableId]);

  const handleChangeCallback = React.useCallback(
    async (event: SelectChangeEvent) => {
      const newValue = event.target.value;
      // Update cache immediately
      localStorageCache.current[tableId] = newValue;
      setCurrentAmount(newValue);
      // Persist to localforage asynchronously
      await localforage.setItem(tableId, newValue);
    },
    [tableId],
  );

  // OKX + MEXC return data from merchant perspective, so columns need to be swapped
  const leftData = isMerchantPerspective ? tableData.weSell : tableData.weBuy;
  const rightData = isMerchantPerspective ? tableData.weBuy : tableData.weSell;

  return (
    <div className="inline-block">
      <div
        className="w-fit rounded-md pb-2 pt-1 select-none relative overflow-hidden"
        style={{
          backgroundColor: "#1a2540",
          border: "1px solid rgba(245,158,11,0.2)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        }}
      >
        {/* subtle gold glow */}
        <div
          style={{
            position: "absolute",
            top: "-40%",
            left: "50%",
            width: "120%",
            height: "80%",
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(245,158,11,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <FormControl
          className="w-full"
          variant="standard"
          sx={{ m: 0, textAlign: "center" }}
        >
          <Select
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            value={currentAmount}
            onChange={handleChangeCallback}
            label="Chọn bảng"
            displayEmpty
          >
            <MenuItem value={""}>
              🔖 Giá Tốt{" "}
              {currentAmount === "" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"150000"}>
              🔖 150k{" "}
              {currentAmount === "150000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"500000"}>
              🔖 500k{" "}
              {currentAmount === "500000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"1000000"}>
              🔖 1 triệu{" "}
              {currentAmount === "1000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"3000000"}>
              🔖 3 triệu{" "}
              {currentAmount === "3000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"5000000"}>
              🔖 5 triệu{" "}
              {currentAmount === "5000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"10000000"}>
              🔖 10 triệu{" "}
              {currentAmount === "10000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            {accessLevel >= 1 && (
              <MenuItem value={"30000000"}>
                🔖 30 triệu{" "}
                {currentAmount === "30000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            <MenuItem value={"50000000"}>
              🔖 50 triệu{" "}
              {currentAmount === "50000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"100000000"}>
              🔖 100 triệu{" "}
              {currentAmount === "100000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            <MenuItem value={"200000000"}>
              🔖 200 triệu{" "}
              {currentAmount === "200000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            {accessLevel >= 1 && (
              <MenuItem value={"300000000"}>
                🔖 300 triệu{" "}
                {currentAmount === "300000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            {accessLevel >= 1 && (
              <MenuItem value={"400000000"}>
                🔖 400 triệu{" "}
                {currentAmount === "400000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            <MenuItem value={"500000000"}>
              🔖 500 triệu{" "}
              {currentAmount === "500000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            {accessLevel >= 1 && (
              <MenuItem value={"600000000"}>
                🔖 600 triệu{" "}
                {currentAmount === "600000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            <MenuItem value={"700000000"}>
              🔖 700 triệu{" "}
              {currentAmount === "700000000" && tableData.different
                ? `(${tableData.different})`
                : ""}
            </MenuItem>
            {accessLevel >= 1 && (
              <MenuItem value={"800000000"}>
                🔖 800 triệu{" "}
                {currentAmount === "800000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            {accessLevel >= 1 && (
              <MenuItem value={"1600000000"}>
                🔖 1.6 tỷ{" "}
                {currentAmount === "1600000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            {accessLevel >= 1 && (
              <MenuItem value={"5000000000"}>
                🔖 5 tỷ{" "}
                {currentAmount === "5000000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
            {accessLevel >= 1 && (
              <MenuItem value={"10000000000"}>
                🔖 10 tỷ{" "}
                {currentAmount === "10000000000" && tableData.different
                  ? `(${tableData.different})`
                  : ""}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        <Box sx={{ width: "100%" }}>
          {progress === 0 && (
            <LinearProgress variant="determinate" value={100} color="info" />
          )}
          {progress === 1 && <LinearProgress color="info" />}
        </Box>

        <div className="flex gap-2 space-x-0 px-2">
          <div className="left text-center">
            <Button
              variant="contained"
              color="success"
              size="small"
              disableElevation
              sx={{
                padding: 0,
                cursor: "default",
              }}
            >
              CAO
            </Button>

            {canShowPrice ? (
              leftData.map((e: P2PAd, index: number) => (
                <React.Fragment key={e.advNo}>
                  <TextPrice
                    data={e}
                    showNickname={filterConfig.showNickname}
                    transAmount={currentAmount}
                    isStale={isStale}
                  />
                  {/* Divider after 10th item (page 1) and 30th item (page 2) */}
                  {(index === 9 || index === 29) && (
                    <div className="border-b border-dashed border-gray-500 my-1" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <></>
            )}
          </div>
          <div className="right text-center">
            <Button
              variant="contained"
              color="primary"
              size="small"
              disableElevation
              sx={{
                padding: 0,
                cursor: "default",
              }}
            >
              THẤP
            </Button>
            {canShowPrice ? (
              rightData.map((e: P2PAd, index: number) => {
                return (
                  <React.Fragment key={e.advNo}>
                    <TextPrice
                      data={e}
                      showNickname={filterConfig.showNickname}
                      transAmount={currentAmount}
                      isStale={isStale}
                    />
                    {/* Divider after 10th item (page 1) and 30th item (page 2) */}
                    {(index === 9 || index === 29) && (
                      <div className="border-b border-dashed border-gray-500 my-1" />
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <FilterBar
        config={filterConfig}
        onConfigChange={handleFilterPersist}
        showNicknameOption={accessLevel >= 1}
        showExtendedRowsOption={accessLevel >= 1}
      />
    </div>
  );
});

function formatMoney(money: unknown): string {
  const rounded = Math.round(money as number);
  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function translateNumberToAcronym(money: number): string {
  const units = [
    { value: 1e9, name: " tỉ" },
    { value: 1e6, name: " triệu" },
    { value: 1e3, name: "k" },
  ];

  const rounded = Math.round(money / 1000) * 1000;

  for (const unit of units) {
    if (rounded >= unit.value) {
      const wholePart = Math.floor(rounded / unit.value);
      const fraction = Math.floor((rounded % unit.value) / (unit.value / 10));

      if (fraction === 0 || unit.name === "k") {
        return `${wholePart} ${unit.name}`;
      } else {
        return `${wholePart}.${fraction}${unit.name}`;
      }
    }
  }

  return rounded.toString();
}

function formatTimeAgo(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} giây trước`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} phút trước`;
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} giờ trước`;
  } else {
    const days = Math.floor(seconds / 86400);
    return `${days} ngày trước`;
  }
}

export default PriceTable;
